import { useIsAuth0AppleMigrationTicketEnabled } from '@/features/flags';
import { useAuth0Session } from '@/hooks/useAuth0Session';
import { assignAppleMigrationTicket } from '@/pages/api/scribeAPI';
import { type RootState } from '@/store';
import { isKindeAppleMigrationAvailable } from '@/utils/appConfiguration';
import { isKindeProvider } from '@/utils/auth/isKindeProvider';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { type AssignAppleMigrationTicketResponse } from '@/types';

// query key
export const GET_APPLE_MIGRATION_TICKET = 'getAppleMigrationTicket';

/**
 * Re-mint interval while the migration modal is open. The backend ticket has a
 * ~15-minute expiry (refreshed on each assign call), so polling every 10 minutes
 * keeps the cached `ticket_id` live — if the user leaves the modal open past the
 * expiry and then clicks confirm, the ticket handed to Auth0 is still valid.
 */
const APPLE_MIGRATION_TICKET_REFRESH_MS = 10 * 60 * 1000;

/** Calls `POST /api/v1/identity-broker/assign-apple-migration-ticket`. */
const fetchAppleMigrationTicket =
  async (): Promise<AssignAppleMigrationTicketResponse> => {
    const { data } = await assignAppleMigrationTicket();
    return data;
  };

/**
 * Calls the assign-apple-migration-ticket endpoint to decide whether to force
 * the Kinde→Auth0 migration modal and to obtain the ticket to hand to Auth0.
 *
 * Note: the endpoint is a POST with side effects — it mints/refreshes a Redis
 * ticket. The backend state machine is designed for idempotent repeated calls
 * (refresh within 1h, re-mint after, absolute 1h+15m lifetime cap), so calling
 * it once per session is safe. The gate lives on `enabled` so it can't be
 * bypassed:
 *  - behind the platform-split Flagsmith flag
 *    (`release-auth0-apple-migration-ticket-web` on web,
 *    `release-auth0-apple-migration-ticket-desktop` on desktop —
 *    see `useIsAuth0AppleMigrationTicketEnabled`),
 *  - only for Kinde-provider users (`isKindeProvider` — handles desktop, where a
 *    Kinde user's `authProvider` is `undefined` rather than `KINDE`),
 *  - only where the Apple identity-migration flow can complete
 *    (`isKindeAppleMigrationAvailable` — excludes Windows desktop),
 *  - waits for the Auth0 session to resolve (`!isAuth0Loading`) so a web Auth0
 *    user is never transiently mis-classified as Kinde,
 *  - `staleTime: Infinity` → at most one call per session.
 *
 * This is a steady-state check (not tied to login), so it also covers users
 * already authenticated when the feature ships, on their next app load.
 *
 * @param keepFresh When true, re-mints the ticket on a fixed interval (see
 * `APPLE_MIGRATION_TICKET_REFRESH_MS`) so a long-open modal never hands Auth0 an
 * expired `ticket_id`. Eligibility consumers (e.g. `GlobalModalsWrapper`) leave
 * it off — only the open modal opts in. The query key is shared, so the modal's
 * polling also keeps the eligibility read current.
 */
export function useQueryAppleMigrationTicket({
  keepFresh = false,
}: { keepFresh?: boolean } = {}) {
  const { token, authProvider } = useSelector((state: RootState) => state.auth);
  const { isLoading: isAuth0Loading } = useAuth0Session();
  const isFlagEnabled = useIsAuth0AppleMigrationTicketEnabled();

  return useQuery({
    queryKey: [GET_APPLE_MIGRATION_TICKET],
    // Enabled on both web and desktop (APP-6026). Desktop reuses this same
    // eligibility mint: the Redux token for a logged-in Kinde desktop user is
    // the Kinde token (kept fresh by `tauriAuth.tsx` via `getToken()`), which
    // `identityBrokerHttp` attaches automatically — so the mint is authenticated
    // without an explicit `getToken()` call. The two platforms differ only in
    // how the Auth0 login *completes*: web watches a same-origin cookie flip in
    // a new tab; desktop completes via the Tauri plugin deep-link → token
    // exchange → `auth:login-success` event (see `KindeMigrationModal` +
    // `src/tauri/auth0/provider.tsx`). `useAuth0Session().isLoading` is hard
    // `false` on desktop, so the `!isAuth0Loading` gate passes there.
    enabled:
      isFlagEnabled &&
      !!token &&
      !isAuth0Loading &&
      isKindeProvider(authProvider) &&
      isKindeAppleMigrationAvailable(),
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: keepFresh ? APPLE_MIGRATION_TICKET_REFRESH_MS : false,
    queryFn: fetchAppleMigrationTicket,
  });
}
